import { Injectable, NgZone } from '@angular/core';
import * as auth from 'firebase/auth';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { getInitials, getUserAvatar, getUserRoles } from '../core/functions';
import { NgxPermissionsService } from 'ngx-permissions';
import { Permission } from '../core/enums/permissions';
import { SharedUserDataService } from '../shared/shared-user-data.service';
import { getUserStorage, removeUserStorage, setDefaultProfileStorage, setUserStorage } from '../core/functions-localStorage';
import { ToastNotifierService } from '../shared/toast-notifier.service';
import { AppUsageService } from '../pages/app-usage/app-usage.service';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userData: any; // Save logged in user data
  constructor(
    public afAuth: AngularFireAuth, // Inject Firebase auth service
    public router: Router,
    private route: ActivatedRoute,
    public ngZone: NgZone, // NgZone service to remove outside scope warning
    public sharedUserDataService: SharedUserDataService,
    private toastNotifier: ToastNotifierService,
    private appUsage: AppUsageService,
  ) {
    /* Saving user data in localstorage when
    logged in and setting up null when logged out */
    this.afAuth.authState.subscribe((user) => {
      if (user) {
        this.appUsage.logApp(user);
        this.userData = user;
        this.sharedUserDataService.updateAvatar({
          url: getUserAvatar(user),
          initials: getInitials(user.displayName),
          fullName: user.displayName,
        });
        setUserStorage(this.userData);
      } else {
        this.sharedUserDataService.clearNotifications();
        this.sharedUserDataService.setWelcomeMessage(false);
        removeUserStorage();
      }
    });
  }
  // Sign in with email/password
  SignIn(email: string, password: string) {
    return this.afAuth
      .signInWithEmailAndPassword(email, password)
      .then((result) => {
        this.navigate();
      })
      .catch((error) => {
        if (error.code == "auth/user-not-found") {
          this.toastNotifier.fail("El usuario especificado no existe en nuestro sistema. Verifica que has ingresado la información correcta.");
        }
        else if (error.code == "auth/wrong-password") {
          this.toastNotifier.fail("Contraseña incorrecta. Por favor, verifica tu contraseña e inténtalo de nuevo.");
        }
        else {
          this.toastNotifier.fail(error.message);
        }
      });
  }
  // Sign up with email/password
  SignUp(email: string, password: string) {
    return this.afAuth
      .createUserWithEmailAndPassword(email, password)
      .then((result) => {
        /* Call the SendVerificaitonMail() function when new user sign
        up and returns promise */
        this.SendVerificationMail();
        this.SetUserData(result.user);
      })
      .catch((error) => {
        window.alert(error.message);
      });
  }
  // Send email verfificaiton when new user sign up
  SendVerificationMail() {
    return this.afAuth.currentUser
      .then((u: any) => u.sendEmailVerification())
      .then(() => {
        this.router.navigate(['verify-email-address']);
      });
  }
  // Reset Forggot password
  ForgotPassword(passwordResetEmail: string) {
    return this.afAuth
      .sendPasswordResetEmail(passwordResetEmail)
      .then(() => {
        this.toastNotifier.success("Te hemos enviado un correo para reetablecer tu contraseña, mira en tu bandeja de entrada.");
      })
      .catch((error) => {
        this.toastNotifier.fail(error.message);
      });
  }
  // Returns true when user is looged in and email is verified
  get isLoggedIn(): boolean {
    const user = getUserStorage();
    return user !== null;
  }
  // Sign in with Google
  GoogleAuth() {
    return this.AuthLogin(new auth.GoogleAuthProvider()).then((res: any) => {
      this.navigate();
    });
  }
  // Auth logic to run auth providers
  AuthLogin(provider: any) {
    return this.afAuth
      .signInWithPopup(provider)
      .then((result) => {
        this.navigate();
      })
      .catch((error) => {
        window.alert(error);
      });
  }
  /* Setting up user data when sign in with username/password,
  sign up with username/password and sign in with social auth
  provider in Firestore database using AngularFirestore + AngularFirestoreDocument service */
  SetUserData(user: any) {
    setDefaultProfileStorage(user.claims)
  }
  // Sign out
  SignOut() {
    this.sharedUserDataService.clearNotifications();
    this.sharedUserDataService.setWelcomeMessage(false);
    removeUserStorage();
    setTimeout(t => {
      return this.afAuth.signOut().then(() => {
        this.router.navigate(['auth/sign-in']);
      });
    }, 100);
  }



  // Método que obtiene los roles del usuario de la sesión y los carga a través de la librería NgxPermissions.
  // Esto les permite tenerlos disponibles en las vistas html y poder gestionar la visibilidad de los diferentes componentes del html en base a los roles
  public static LoadUserRoles(user: any, permissionsService: NgxPermissionsService) {
    var userData = user;
    if (userData) {
      const perm = userData;
      if (perm) {
        if (Array.isArray(perm)) {
          permissionsService.loadPermissions(perm);
        }
        else {
          permissionsService.loadPermissions([perm]);
        }
        // Almacenamos los permisos del usuario en el localStorage para tenerlos disponibles
        window.localStorage.setItem("permissions", JSON.stringify(perm));
      }
    }
  }

  hasRoles(targetRoles: string[]) {
    if (!this.userData)
      return false;

    //obtenemos el rol del usuario que puede ser uno o varios roles
    const perm = Array.isArray(this.userData) ? this.userData : [this.userData];
    if (perm && targetRoles) {
      // Se guarda la lista de roles a comprobar en un array para facilitar su manejo
      const rolesArray = Array.isArray(targetRoles) ? targetRoles : [targetRoles];
      return perm.some((x) => rolesArray.includes(x));
    }

    return false;
  }

  navigate() {
    this.afAuth.idTokenResult.subscribe((user) => {
      if (user) {
        this.SetUserData(user);
        if (getUserRoles(user.claims).includes(Permission.Admin)) {
          this.sharedUserDataService.setWelcomeMessage(false);
          this.router.navigate(['tenants']);
        }
        else {
          const url = this.route.snapshot.queryParamMap.has('x')
            ? this.route.snapshot.queryParamMap.get('x')
            : 'dashboard';

          this.sharedUserDataService.setWelcomeMessage(true);
          this.router.navigate([url]);
        }
      }
    });
  }
}
